import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import css from 'styled-jsx/css';
import { colors } from '../Theme';

const styles = css`
  .button {
    border-radius: 10px 0 10px 0;
    box-sizing: border-box;
    border: rgb(${colors.ywgPinkishOrangeRgb}) 2px solid;
    background-color: rgb(${colors.ywgPinkishOrangeRgb});
    color: white;
    font-weight: bold;
    text-align: center;
    margin: 1.25rem 0px 0px;
    padding: 18px 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
  .button:hover,
  .button:focus {
    text-decoration: none;
    border-color: white;
    box-shadow: 0 0 10px 0 rgba(${colors.ywgPinkishOrangeRgb}, 0.6);
    outline: none;
  }
  .button--secondary {
    background-color: white;
    color: rgb(${colors.ywgNavyRgb});
    border-color: rgba(${colors.ywgPinkishOrangeRgb}, 0.5);
  }
  .button--secondary:hover,
  .button--secondary:focus {
    background-color: ${colors.ywgPinkishOrangeSecondary};
    border-color: ${colors.ywgPrimary};
    box-shadow: none;
  }
  .button--square {
    border-radius: initial;
  }
  .button--shadowed {
    box-shadow: 0 8px 34px 0 rgba(${colors.ywgPinkishOrangeRgb}, 0.6);
  }
  .button--arrow-right::after {
    content: '';
    background-image: url('/static/images/icons/arrow-white.svg');
    height: 1rem;
    width: 18px;
    display: inline-block;
    vertical-align: middle;
    margin-left: 0.625rem;
    color: ${colors.ywgNavy};
  }

  .button--arrow-left::before {
    content: '';
    transform: rotate(180deg);
    background-image: url('/static/images/icons/arrow-white.svg');
    height: 1rem;
    width: 18px;
    display: inline-block;
    vertical-align: middle;
    margin-right: 0.625rem;
    color: ${colors.ywgNavy};
  }

  .button--secondary.button--arrow-right::after,
  .button--secondary.button--arrow-left::before {
    background-image: url('/static/images/icons/arrow-navy.svg');
  }

  .button[disabled]:not(.button--submitting),
  .button--disabled {
    opacity: 0.2;
    cursor: not-allowed;
  }
  .button--submitting {
    cursor: progress;
    opacity: 1;
  }
  .button.button--submitting,
  .button:active {
    color: white;
    background-color: ${colors.ywgPinkishOrangeStrong};
    border-color: ${colors.ywgPinkishOrangeStrong};
    box-shadow: none;
  }
`;

const Button = React.forwardRef(
  (
    {
      className,
      children,
      text,
      shape,
      shadowed,
      secondary,
      arrow,
      submitting,
      submittingMessage,
      disabled,
      ...props
    },
    ref
  ) => {
    if (submitting) {
      children = submittingMessage;
      disabled = true;
    }
    const tagClasses = classnames(className, 'button', {
      'button--shadowed': shadowed,
      'button--square': shape === 'square',
      'button--secondary': secondary,
      'button--arrow-left': arrow === 'left',
      'button--arrow-right': arrow === 'right',
      'button--disabled': disabled,
      'button--submitting': submitting
    });

    if (props.href) {
      return (
        <a {...props} ref={ref} className={tagClasses}>
          {children}
          <style jsx>{styles}</style>
        </a>
      );
    }
    if (props.htmlFor) {
      return (
        <label {...props} ref={ref} className={tagClasses}>
          {children}
          <style jsx>{styles}</style>
        </label>
      );
    }
    return (
      <button {...props} value={children || text} className={tagClasses}>
        {children}
        <style jsx>{styles}</style>
      </button>
    );
  }
);

Button.propTypes = {
  className: PropTypes.string,
  /**
   * Text of the button
   */
  text: PropTypes.string,
  /**
   * shape of the button
   */
  shape: PropTypes.oneOf(['square']),
  /**
   * css height
   */
  arrow: PropTypes.oneOf(['left', 'right']),
  /**
   * Adds a shadow to the button
   */
  shadowed: PropTypes.bool,

  /**
   * Applies secondary theme
   */
  secondary: PropTypes.bool,

  submitting: PropTypes.bool,
  submittingMessage: PropTypes.string,
  disabled: PropTypes.bool
};

Button.defaultProps = {
  shadowed: false,
  secondary: false,
  submitting: false,
  submittingMessage: 'Envoi en cours…',
  disabled: false
};

export default Button;
